import { formatCurrency } from '@/utils/dateUtils'
import type { BulkPreview, BulkSelection } from './bulk-actions.types'

/** The fleet previews at most this many at once, and starts at most BULK_START_CHUNK per request. */
export const BULK_PREVIEW_MAX = 100
export const BULK_START_CHUNK = 12

/** The bulk actions a page offers on tickets: the ones that make sense on many at once. */
export const TICKET_BULK_ACTIONS: Omit<BulkSelection, 'targets'>[] = [
  { action: 'ticket_test', label: 'Test in staging', env: 'staging' },
  { action: 'ticket_test', label: 'Test in dev', env: 'dev' },
  { action: 'ticket_review', label: 'Review' },
  { action: 'ticket_validity', label: 'Still valid?' },
  { action: 'ticket_clean', label: 'Clean up' }
]

/** What a pass does to a ticket, for the actions that move one: Test moves a pass to Done or Done in Staging. */
export function bulkOutcomeText(selection: Pick<BulkSelection, 'action' | 'env'>): string {
  if (selection.action !== 'ticket_test') return ''
  const done = selection.env === 'staging' ? 'Done in Staging' : 'Done'
  return `A ticket that passes moves to ${done}; one that fails is not moved and waits for a person to confirm the failure.`
}

export function bulkActionId(selection: Pick<BulkSelection, 'action' | 'env'>): string {
  return selection.env ? `${selection.action}:${selection.env}` : selection.action
}

/** "3 fleet runs, ~$12.00, 0 Jira writes" — said before anything starts. */
export function previewHeadline(
  preview: Pick<BulkPreview, 'runs' | 'estimateUsd' | 'jiraWrites'>
): string {
  const runs = `${preview.runs} fleet run${preview.runs === 1 ? '' : 's'}`
  const writes = `${preview.jiraWrites} Jira write${preview.jiraWrites === 1 ? '' : 's'}`
  return `${runs}, ~${formatCurrency(preview.estimateUsd)}, ${writes}`
}

/** Why fewer runs start than were picked: the ones that do not apply, and the daily cap. */
export function previewShortfall(preview: BulkPreview): string[] {
  const out: string[] = []
  const refused = preview.items.length - preview.eligible
  if (refused)
    out.push(`${refused} of ${preview.items.length} cannot start now; each says why below.`)
  if (preview.cap.perDay === 0)
    out.push('Actions from My work are turned off in the fleet settings.')
  else if (preview.eligible > preview.runs) {
    out.push(
      `Only ${preview.runs} fit what is left of today's ${preview.cap.perDay} actions; the rest wait for tomorrow.`
    )
  }
  return out
}

/** The targets to start: the eligible ones, in the order picked, as many as the cap allows. */
export function targetsToStart(preview: BulkPreview): string[] {
  return preview.items
    .filter(item => item.ok)
    .map(item => item.target)
    .slice(0, preview.runs)
}

export function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let start = 0; start < items.length; start += size)
    chunks.push(items.slice(start, start + size))
  return chunks
}
